import { computeTextEdit } from "./textDiff.js";
import { appendBlock, addGalleryItem as addGalleryItemInDoc, addTodoItem as addTodoItemInDoc, blocksFromYDoc, commentsFromYDoc, getBlockApprovalsMap, getBlockChoicesArray, getBlockMetaMap, getBlockText, getBlockTitle, getBlockVotesMap, LOCAL_YJS_ORIGIN, postComment as postCommentInDoc, removeBlock as removeBlockFromDoc, moveBlock as moveBlockInDoc, moveBlockToIndex as moveBlockToIndexInDoc, removeComment as removeCommentInDoc, removeGalleryItem as removeGalleryItemInDoc, removeTodoItem as removeTodoItemInDoc, reorderGalleryItem as reorderGalleryItemInDoc, reorderTodoItem as reorderTodoItemInDoc, setGalleryItemCaption as setGalleryItemCaptionInDoc, setGalleryItemMeta as setGalleryItemMetaInDoc, setBlockParent as setBlockParentInDoc, setTodoItemAssignees as setTodoItemAssigneesInDoc, setTodoItemDueDate as setTodoItemDueDateInDoc, setTodoItemPriority as setTodoItemPriorityInDoc, setTodoItemTitle as setTodoItemTitleInDoc, toggleReaction as toggleReactionInDoc, toggleTodoItemDone as toggleTodoItemDoneInDoc, } from "./waveYDoc.js";
export class WaveDocEditor {
    doc;
    revision = 0;
    listeners = new Set();
    constructor(doc) {
        this.doc = doc;
        this.doc.on("update", () => {
            this.revision += 1;
            for (const listener of this.listeners)
                listener();
        });
    }
    get blocks() {
        return blocksFromYDoc(this.doc);
    }
    get comments() {
        return commentsFromYDoc(this.doc);
    }
    get contentRevision() {
        return this.revision;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    setText(blockId, field, value) {
        const yText = field === "title" ? getBlockTitle(this.doc, blockId) : getBlockText(this.doc, blockId);
        const current = yText.toString();
        if (current === value)
            return;
        const edit = computeTextEdit(current, value);
        if (!edit)
            return;
        this.doc.transact(() => {
            if (edit.deleteCount > 0) {
                yText.delete(edit.start, edit.deleteCount);
            }
            if (edit.insert.length > 0) {
                yText.insert(edit.start, edit.insert);
            }
        }, LOCAL_YJS_ORIGIN);
    }
    setVote(blockId, sub, choice) {
        this.doc.transact(() => {
            getBlockVotesMap(this.doc, blockId).set(sub, choice);
        }, LOCAL_YJS_ORIGIN);
    }
    setApproval(blockId, sub, approved) {
        this.doc.transact(() => {
            getBlockApprovalsMap(this.doc, blockId).set(sub, approved);
        }, LOCAL_YJS_ORIGIN);
    }
    setTaskDone(blockId, done) {
        this.doc.transact(() => {
            getBlockMetaMap(this.doc, blockId).set("taskDone", done);
        }, LOCAL_YJS_ORIGIN);
    }
    addTodoItem(blockId, authorSub) {
        return addTodoItemInDoc(this.doc, blockId, { authorSub });
    }
    setTodoItemTitle(blockId, itemId, title) {
        setTodoItemTitleInDoc(this.doc, blockId, itemId, title);
    }
    toggleTodoItemDone(blockId, itemId, done) {
        toggleTodoItemDoneInDoc(this.doc, blockId, itemId, done);
    }
    setTodoItemAssignees(blockId, itemId, assignees) {
        setTodoItemAssigneesInDoc(this.doc, blockId, itemId, assignees);
    }
    setTodoItemDueDate(blockId, itemId, dueDateISO) {
        setTodoItemDueDateInDoc(this.doc, blockId, itemId, dueDateISO);
    }
    setTodoItemPriority(blockId, itemId, priority) {
        setTodoItemPriorityInDoc(this.doc, blockId, itemId, priority);
    }
    removeTodoItem(blockId, itemId) {
        removeTodoItemInDoc(this.doc, blockId, itemId);
    }
    reorderTodoItem(blockId, itemId, toIndex) {
        reorderTodoItemInDoc(this.doc, blockId, itemId, toIndex);
    }
    addGalleryItem(blockId, input = {}) {
        return addGalleryItemInDoc(this.doc, blockId, input);
    }
    setGalleryItemMeta(blockId, itemId, patch) {
        setGalleryItemMetaInDoc(this.doc, blockId, itemId, patch);
    }
    setGalleryItemCaption(blockId, itemId, caption) {
        setGalleryItemCaptionInDoc(this.doc, blockId, itemId, caption);
    }
    removeGalleryItem(blockId, itemId) {
        removeGalleryItemInDoc(this.doc, blockId, itemId);
    }
    reorderGalleryItem(blockId, itemId, toIndex) {
        reorderGalleryItemInDoc(this.doc, blockId, itemId, toIndex);
    }
    setChoices(blockId, choices, previousChoices) {
        this.doc.transact(() => {
            const votes = getBlockVotesMap(this.doc, blockId);
            if (previousChoices) {
                for (let i = 0; i < choices.length; i += 1) {
                    const oldChoice = previousChoices[i];
                    const newChoice = choices[i];
                    if (!oldChoice || !newChoice || oldChoice === newChoice)
                        continue;
                    votes.forEach((value, key) => {
                        if (value === oldChoice)
                            votes.set(key, newChoice);
                    });
                }
            }
            const arr = getBlockChoicesArray(this.doc, blockId);
            while (arr.length > 0)
                arr.delete(0, 1);
            for (const choice of choices)
                arr.push([choice]);
        }, LOCAL_YJS_ORIGIN);
    }
    setMetaString(blockId, key, value) {
        this.doc.transact(() => {
            const meta = getBlockMetaMap(this.doc, blockId);
            if (value == null)
                meta.delete(key);
            else
                meta.set(key, value);
        }, LOCAL_YJS_ORIGIN);
    }
    setMetaBool(blockId, key, value) {
        this.doc.transact(() => {
            const meta = getBlockMetaMap(this.doc, blockId);
            if (value == null)
                meta.delete(key);
            else
                meta.set(key, value);
        }, LOCAL_YJS_ORIGIN);
    }
    setMetaInt(blockId, key, value) {
        this.doc.transact(() => {
            const meta = getBlockMetaMap(this.doc, blockId);
            if (value == null)
                meta.delete(key);
            else
                meta.set(key, value);
        }, LOCAL_YJS_ORIGIN);
    }
    prepareFileBlockUpload(blockId, fileName, mimeType, fileSize) {
        this.doc.transact(() => {
            const meta = getBlockMetaMap(this.doc, blockId);
            meta.set("fileName", fileName);
            meta.set("mimeType", mimeType);
            meta.set("fileSize", fileSize);
            meta.set("uploadStatus", "uploading");
            meta.set("ragStatus", "none");
        }, LOCAL_YJS_ORIGIN);
    }
    completeFileBlockUpload(blockId, s3Key) {
        this.doc.transact(() => {
            const meta = getBlockMetaMap(this.doc, blockId);
            meta.set("s3Key", s3Key);
            meta.set("uploadStatus", "ready");
        }, LOCAL_YJS_ORIGIN);
    }
    appendBlock(kind, afterBlockId, opts) {
        let block = null;
        this.doc.transact(() => {
            block = appendBlock(this.doc, kind, afterBlockId ?? undefined, opts);
        }, LOCAL_YJS_ORIGIN);
        return block;
    }
    attachBlock(parentId, kind, relation) {
        return this.appendBlock(kind, null, { parentId, relation: relation ?? undefined });
    }
    setBlockParent(blockId, parentId, relation) {
        setBlockParentInDoc(this.doc, blockId, parentId, relation);
    }
    archiveBlock(blockId) {
        const now = new Date().toISOString();
        this.doc.transact(() => {
            const meta = getBlockMetaMap(this.doc, blockId);
            meta.set("archived", true);
            meta.set("archivedAt", now);
        }, LOCAL_YJS_ORIGIN);
    }
    restoreBlock(blockId) {
        this.doc.transact(() => {
            const meta = getBlockMetaMap(this.doc, blockId);
            meta.delete("archived");
            meta.delete("archivedAt");
        }, LOCAL_YJS_ORIGIN);
    }
    removeBlock(blockId) {
        this.doc.transact(() => {
            removeBlockFromDoc(this.doc, blockId);
        }, LOCAL_YJS_ORIGIN);
    }
    moveBlock(blockId, direction) {
        return moveBlockInDoc(this.doc, blockId, direction);
    }
    moveBlockToIndex(blockId, targetTopLevelBlockId, place) {
        return moveBlockToIndexInDoc(this.doc, blockId, targetTopLevelBlockId, place);
    }
    postComment(blockAnchorId, body, parentId, authorSub, authorName, galleryItemId) {
        const trimmed = body.trim();
        if (!trimmed) {
            throw new Error("Comment body is empty");
        }
        return postCommentInDoc(this.doc, {
            blockAnchorId,
            body: trimmed,
            parentId,
            galleryItemId,
            authorSub,
            authorName,
        });
    }
    toggleReaction(commentId, emoji, sub) {
        toggleReactionInDoc(this.doc, commentId, emoji, sub);
    }
    removeComment(commentId) {
        removeCommentInDoc(this.doc, commentId);
    }
}
